import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { EventService } from '@base/services/private/event.service';
import { ApiService } from '@base/services/private/api.service';
import { API_ENDPOINTS } from '@base/constants/api-endpoints';
import { catchError, map, Observable, of, retry, switchMap, tap, timeout, TimeoutError, timer } from 'rxjs';
import { HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { AuthService } from '@core/services/auth.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { SessionService } from '@base/services/private/session.service';
import { REQUEST } from '@core/models/requests';
import { SupportedLanguages } from '@config/supported-languages.enum';
import { TranslateService } from '@base/services/private/translate.service';
import { Validate } from '@base/models/responses/validate.res';
import { LoginLoader, MessageLoader } from '@base/models/responses/login-loader.res';
import { colors } from '@base/constants/common';
import { ThemeService } from '@base/services/private/theme.service';
import { CodeType } from '@base/models/responses/auth-response';
import { GUARD_API_TIMEOUT_MS, RETRY_DELAYS_MS } from '@base/constants/config';
import { MainMenuRoute } from '@base/enums/main-menu-route.enum';

/**
 * Route guard for validating event existence and authenticating the user.
 *
 * Validates the event from route params and authenticates using `passcode` and `logincode`.
 * Custom whitelabel event directories are canonicalized before the validation API URL and auth
 * session are created, so mixed-case URLs like `/ai4-match` still validate and load `Ai4-Match`.
 * Sets up event context, session, and language on success. Redirects to root on failure.
 *
 * @param route - Snapshot of the activated route with parameters and query params.
 * @returns Observable that emits `true` if access is granted, or a `UrlTree` for redirection.
 */
export const isValidEventGuard: CanActivateFn = (route) => {
  const apiSrv = inject(ApiService);
  const globalStateSrv = inject(GlobalStateService);
  const translateSrv = inject(TranslateService);
  const sessionSrv = inject(SessionService);
  const authSrv = inject(AuthService);
  let eventDirectory = route.params['eventDirectory'] ?? '';
  const eventSrv = inject(EventService);
  const router = inject(Router);
  const passcode = route.queryParamMap.get('passcode') ?? '';
  const logincode = route.queryParamMap.get('logincode') ?? '';
  const platform = route.queryParamMap.get('platform') ?? '';
  const provider = route.queryParamMap.get('provider') ?? '';
  const isExternalMode = route.queryParamMap.get('mode') === 'external';
  const event = eventSrv.data;
  const themeSrv = inject(ThemeService);
  const isBrandedDomain = event?.isBrandedDomain ?? false;

  const getCurrentFullUrl = (): string => {
    const navigation = router.getCurrentNavigation();
    const url = navigation?.extractedUrl?.toString() ?? '';
    return encodeURIComponent(url);
  };

  const currentUrl = getCurrentFullUrl();

  if (isBrandedDomain) {
    eventDirectory = event.name;
  }

  const normalizedEventKey = eventSrv.resolveCustomKey(eventDirectory);
  eventDirectory = normalizedEventKey ?? eventDirectory;

  const API = API_ENDPOINTS.BASE_URL.replace('event-name', `${eventDirectory}/`);
  const langSession = String(sessionSrv.getItem('lang'));

  eventSrv.set({
    name: eventDirectory,
    isCustom: !!normalizedEventKey,
    isBrandedDomain: isBrandedDomain,
  });

  const initializeAPI = (): Observable<HttpResponse<unknown>> => {
    const payload: REQUEST = {
      api: API,
      observe: 'response',
      responseType: 'text',
    };

    if (logincode) {
      payload.params = {
        logincode,
      };
    }

    if (passcode) {
      payload.params = {
        passcode,
      };
    }

    if (provider) {
      payload.params = {
        ...payload.params,
        provider,
      };
    }

    if (platform) {
      payload.params = {
        ...payload.params,
        platform,
      };
    }

    const request$ = apiSrv.get<HttpResponse<unknown>>(payload).pipe(
      // Each retry attempt gets its own independent timeout window.
      timeout(GUARD_API_TIMEOUT_MS)
    );

    // Skip retry when:
    // - No :eventDirectory param → branded route '' matched.
    // - skipRetry flag → navigation came from not-found; user already saw 404, fail fast.
    const skipRetry = router.getCurrentNavigation()?.extras?.state?.['skipRetry'] === true;
    if (!route.params['eventDirectory'] || skipRetry) {
      return request$;
    }

    return request$.pipe(
      retry({
        count: RETRY_DELAYS_MS.length,
        delay: (err: HttpErrorResponse | TimeoutError, retryCount) => {
          const isRetryable =
            err instanceof TimeoutError ||
            (err instanceof HttpErrorResponse && (err.status === 0 || err.status >= 500));
          if (isRetryable) {
            const delayMs = RETRY_DELAYS_MS[retryCount - 1];
            console.warn(`[Guard] retry ${retryCount}/${RETRY_DELAYS_MS.length} in ${delayMs / 1000}s`);
            return timer(delayMs);
          }
          throw err;
        },
      })
    );
  };

  /**
   * Handles the authentication success logic, setting state and language.
   * This function is extracted to avoid repetition.
   * @param authRes -  The authentication response object.
   */
  const handleAuthSuccess = (authRes: Validate): void => {
    globalStateSrv.setState({
      validate: authRes,
    });
    const newCode = !!logincode || !!passcode;
    authSrv.setAuthSession(newCode, authRes.hashed_id, eventDirectory);

    const isValidLang = authRes.event_lang.includes(langSession);
    if (isValidLang && authRes.anon) {
      translateSrv.setLanguage(langSession || SupportedLanguages.EN);
    } else {
      const defaultLanguage = authRes?.event_lang?.length > 1 ? SupportedLanguages.EN : authRes.event_lang[0];
      translateSrv.setLanguage((authRes.person_lang || defaultLanguage) ?? SupportedLanguages.EN);
    }

    // set color event and bg event
    updateThemeColor(authRes);
  };

  /**
   * Updates the theme colors based on authentication response.
   * @param authRes - The authentication response object.
   */
  const updateThemeColor = (authRes: Validate) => {
    const colorEvent = authRes?.event_color ?? colors.primary;
    const bgColorEvent = authRes?.event_background ?? colors.light;
    themeSrv.updateThemeColor(colorEvent, bgColorEvent);
  };

  /**
   * Handles the login loader success logic, setting state.
   * @param loader - The login loader object.
   */
  const handleLoaderSuccess = (loader: LoginLoader): void => {
    globalStateSrv.setState({
      loader,
    });
  };

  /**
   * Handles the login loader success logic, setting state.
   * @param loader - The login loader object.
   */
  const handleMessageLoaderSuccess = (messageLoader: MessageLoader): void => {
    globalStateSrv.setState({
      messageLoader,
    });
  };

  /**
   * Handles the authentication error logic, clearing session and redirecting.
   * This function is extracted to avoid repetition.
   * @returns An Observable of a UrlTree for redirection.
   */
  const handleValidateError = (): Observable<UrlTree> => {
    translateSrv.setLanguage(SupportedLanguages.EN);
    sessionSrv.clear();
    return of(router.createUrlTree(['not-found'], { queryParams: { from: currentUrl } }));
  };

  /**
   * Handle validate 2FA or Old auth using passcode
   * @param res - Validate
   * @returns Observable<Validate>
   */
  const handleValidate2FA = (res: Validate): Observable<Validate> => {
    const eventCode = res.event_id;
    if (res.anon) {
      return authSrv.authInfo(passcode, eventCode).pipe(
        switchMap((info) => {
          if (passcode && info.auth_channel.includes(CodeType.PASSCODE.toLowerCase())) {
            return authSrv.loginWithPasscode(passcode, provider, platform);
          }
          return of(res);
        }),
        catchError(() => of(res)) // In case of error, proceed with the original validation response
      );
    } else {
      return of(res);
    }
  };

  /**
   * Handle loader for web app
   * @param res - Validate
   */
  const handleLoader = (res: Validate): Observable<boolean | UrlTree> => {
    if (res.hashed_id) {
      return authSrv.getLoginLoader().pipe(
        tap(handleLoaderSuccess),
        switchMap(() => {
          // only load for non-anon user
          // no need hit this api if accessing from external mode
          if (!res.anon && !isExternalMode) {
            return authSrv.getMessageLoader().pipe(
              tap(handleMessageLoaderSuccess),
              map(() => true),
              catchError(() => handleValidateError())
            );
          }
          return of(true);
        }),
        catchError(() => handleValidateError())
      );
    }
    return of(true);
  };

  // skip validation if it's for mini app
  const auth =
    passcode && isExternalMode
      ? authSrv.loginWithPasscode(passcode, provider, platform)
      : authSrv.loginWithLogincode(logincode, provider, platform);

  /**
   * Performs the login operation and handles subsequent logic.
   * This function is extracted to avoid repetition.
   * @returns An Observable that emits `true` if login is successful, or a `UrlTree` for redirection.
   */
  const login: Observable<boolean | UrlTree> = auth.pipe(
    switchMap((res) => {
      if (!res) {
        return handleValidateError();
      }
      handleAuthSuccess(res);
      return of(res);
    }),
    switchMap((res) => {
      if (!res) return handleValidateError();
      // Ensure res is a Validate object, not a UrlTree
      return handleValidate2FA(res as Validate);
    }),
    switchMap((res) => {
      // Ensure res is a Validate object, not a UrlTree
      if (!res) return handleValidateError();
      return handleLoader(res as Validate);
    }),
    catchError(handleValidateError) // Handle errors during the passcode login
  );

  // If eventDirectory is a MainMenuRoute value, loadInitEventDir branded-domain API failed
  // and the fallback incorrectly matched a page route as the event dir. Skip the API call
  // entirely — it will always fail and only adds CORS noise.
  const isPageRoute = Object.values(MainMenuRoute).includes(eventDirectory as MainMenuRoute);
  if (isPageRoute) {
    sessionSrv.clear();
    return of(router.createUrlTree(['not-found'], { queryParams: { from: currentUrl } }));
  }

  return initializeAPI().pipe(
    switchMap((res: HttpResponse<unknown>) => {
      if (res.status === HttpStatusCode.Ok || res.status === HttpStatusCode.Found) {
        return login;
      } else {
        sessionSrv.clear();
        return of(router.createUrlTree(['not-found'], { queryParams: { from: currentUrl } }));
      }
    }),
    catchError((err: HttpErrorResponse) => {
      console.error('Error validation:', err);
      sessionSrv.clear();
      return of(router.createUrlTree(['not-found'], { queryParams: { from: currentUrl } }));
    })
  );
};
